import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Hand, MessageCircle, X, Flag, Ban, Lock, Sparkles, ChevronRight, Award, TrendingUp, Send, Leaf, UserPlus, UserCheck, Play, Trash2 } from 'lucide-react';
import { mediaSrc } from '@/components/CommunityMedia';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const img = (u) => (u ? (u.startsWith('http') ? u : `${BACKEND_URL}${u}`) : null);
export const isPremiumUser = (user) => !!(user?.subscription_active && ['premium', 'pro'].includes(user?.subscription_tier));

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ============ Living Garden Stream ============
export function GardenStream({ user, onUpgrade, embedded }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ can_interact: false, posts: [] });
  const [sprouts, setSprouts] = useState([]);
  const [activeStory, setActiveStory] = useState(null);
  const [openProfile, setOpenProfile] = useState(null);
  const [active, setActive] = useState(0);
  const scrollerRef = useRef(null);
  const pausedRef = useRef(false);
  const premium = data.can_interact;

  const load = useCallback(async () => {
    try {
      const [s, sp] = await Promise.all([
        axios.get(`${API}/community/stream`, { withCredentials: true }),
        axios.get(`${API}/community/sprouts`, { withCredentials: true }),
      ]);
      setData(s.data || { can_interact: false, posts: [] });
      setSprouts(sp.data || []);
    } catch (e) { /* stream is best-effort */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-cycle the stream
  useEffect(() => {
    const posts = data.posts || [];
    if (posts.length < 2) return;
    const id = setInterval(() => {
      if (pausedRef.current || activeStory || openProfile) return;
      setActive((prev) => {
        const next = (prev + 1) % posts.length;
        const el = scrollerRef.current?.children?.[next];
        if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        return next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [data.posts, activeStory, openProfile]);

  const nudge = async (postId) => {
    if (!premium) { onUpgrade?.(); return; }
    try {
      const r = await axios.post(`${API}/community/posts/${postId}/nudge`, {}, { withCredentials: true });
      setData((d) => ({ ...d, posts: d.posts.map((p) => p.id === postId ? { ...p, viewer_nudged: r.data.nudged, nudge_count: r.data.nudge_count } : p) }));
    } catch (e) { /* noop */ }
  };

  const posts = data.posts || [];

  return (
    <div data-testid="garden-stream">
      {/* Daily Sprouts rings */}
      <SproutsRow user={user} sprouts={sprouts} premium={premium} onOpen={setActiveStory} onUpgrade={onUpgrade} />

      {/* Living Garden Stream */}
      {posts.length > 0 ? (
        <div className="mt-4">
          {!embedded && (
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-[#6B9F4A]/15 flex items-center justify-center">
                <Leaf className="w-4 h-4 text-[#6B9F4A]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#1A4331] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Living Garden Stream</h3>
                <p className="text-xs text-[#7A6B57]">Fresh from public gardens near you</p>
              </div>
            </div>
          )}
          <div
            ref={scrollerRef}
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
            className="flex gap-3 overflow-x-auto pb-2 snap-x"
            style={{ scrollbarWidth: 'thin' }}
            data-testid="stream-scroller"
          >
            {posts.map((p, i) => (
              <StreamCard
                key={p.id}
                post={p}
                active={i === active}
                premium={premium}
                onNudge={() => nudge(p.id)}
                onOpen={() => navigate(`/community/post/${p.id}`)}
                onAuthor={() => p.author_public && p.author_id && setOpenProfile(p.author_id)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {activeStory && <StoryViewer sprout={activeStory} onClose={() => setActiveStory(null)} onReported={load} onProfile={(id) => { setActiveStory(null); setOpenProfile(id); }} />}
      {openProfile && <GrowerProfileModal userId={openProfile} onClose={() => setOpenProfile(null)} />}
    </div>
  );
}

function StreamCard({ post: p, active, premium, onNudge, onOpen }) {
  const photo = img(p.photo_url);
  const ringCls = active ? 'ring-2 ring-[#C9A85B] border-transparent scale-[1.01]' : 'border-[#1A4331]/10';
  if (!photo) {
    // Photoless / general update — text-forward cream card so it never renders blank
    return (
      <div className={`relative flex-shrink-0 w-60 h-72 rounded-3xl overflow-hidden snap-center transition-all duration-300 cursor-pointer border p-4 flex flex-col ${ringCls}`}
        data-testid={`stream-card-${p.id}`} onClick={onOpen} style={{ background: 'linear-gradient(160deg,#FAF6EC,#EFE9D7)' }}>
        <span className={`inline-flex items-center gap-1 self-start text-[10px] font-bold px-2 py-0.5 rounded-full ${p.post_type === 'note' ? 'bg-[#6B9F4A]/15 text-[#4E7A33]' : 'bg-[#C9A85B]/18 text-[#8A6D2B]'}`}>
          {p.post_type === 'note' ? <MessageCircle className="w-3 h-3" /> : <Leaf className="w-3 h-3" />} {p.post_type === 'note' ? 'UPDATE' : (p.crop_name || 'HARVEST')}
        </span>
        <p className="text-[15px] text-[#1A4331] leading-snug mt-3 flex-1 line-clamp-6 font-medium">{p.note || p.crop_name}</p>
        <p className="text-xs text-[#7A6B57] mt-2 truncate">{p.display_name} · {p.region}</p>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={(e) => { e.stopPropagation(); onNudge(); }} data-testid={`stream-nudge-${p.id}`}
            className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-colors ${p.viewer_nudged ? 'bg-[#C9A85B] text-[#1A4331]' : 'bg-[#1A4331]/8 hover:bg-[#1A4331]/14 text-[#1A4331]'}`}>
            {premium ? <Hand className="w-3.5 h-3.5" /> : <Lock className="w-3 h-3" />} {p.nudge_count > 0 ? p.nudge_count : 'Nudge'}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-[#1A4331]/8 hover:bg-[#1A4331]/14 text-[#1A4331]">
            <MessageCircle className="w-3.5 h-3.5" /> {p.comment_count > 0 ? p.comment_count : ''}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`relative flex-shrink-0 w-60 h-72 rounded-3xl overflow-hidden snap-center transition-all duration-300 cursor-pointer border ${ringCls}`}
      data-testid={`stream-card-${p.id}`}
      onClick={onOpen}
    >
      <img src={photo} alt={p.crop_name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
      {(p.crop_name || p.post_type === 'note') && (
        <div className="absolute top-3 left-3 bg-[#FAF6EC]/90 text-[#1A4331] text-[11px] font-semibold px-2.5 py-1 rounded-full">
          {p.post_type === 'note' ? 'Update' : `${p.crop_name}${p.variety ? ` · ${p.variety}` : ''}`}
        </div>
      )}
      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
        <p className="text-xs font-medium opacity-90">{p.display_name} · {p.region}</p>
        {p.note && <p className="text-sm mt-1 line-clamp-2 leading-snug">{p.note}</p>}
        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={(e) => { e.stopPropagation(); onNudge(); }}
            data-testid={`stream-nudge-${p.id}`}
            className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full transition-colors ${p.viewer_nudged ? 'bg-[#C9A85B] text-[#1A4331]' : 'bg-white/20 hover:bg-white/30 text-white'}`}
          >
            {premium ? <Hand className="w-3.5 h-3.5" /> : <Lock className="w-3 h-3" />} {p.nudge_count > 0 ? p.nudge_count : 'Nudge'}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-white">
            <MessageCircle className="w-3.5 h-3.5" /> {p.comment_count > 0 ? p.comment_count : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Daily Sprouts row (story rings) ============
function SproutsRow({ user, sprouts, premium, onOpen, onUpgrade }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-[#C9A85B]" />
        <h3 className="text-sm font-semibold text-[#1A4331] uppercase tracking-wide">Daily Sprouts</h3>
        <span className="text-[11px] text-[#7A6B57]">· 24-hour garden updates</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2" data-testid="sprouts-row">
        {/* Add your own */}
        <a href="/planner" className="flex flex-col items-center gap-1.5 flex-shrink-0 w-[68px]" data-testid="add-sprout-link">
          <div className="w-16 h-16 rounded-full border-2 border-dashed border-[#8EAA97] flex items-center justify-center bg-[#FAF6EC]">
            <span className="text-2xl text-[#6B9F4A] leading-none">+</span>
          </div>
          <span className="text-[10px] text-[#7A6B57] text-center leading-tight">Your plant</span>
        </a>
        {sprouts.length === 0 ? (
          <div className="flex items-center px-3">
            <p className="text-xs text-[#7A6B57] italic">No sprouts yet — add a 24h photo of a plant from your Garden Planner.</p>
          </div>
        ) : sprouts.map((s) => (
          <button key={s.id} onClick={() => onOpen(s)} data-testid={`sprout-ring-${s.id}`} className="flex flex-col items-center gap-1.5 flex-shrink-0 w-[68px]">
            <div className="w-16 h-16 rounded-full p-[3px]" style={{ background: 'linear-gradient(135deg, #C9A85B, #6B9F4A, #C66B4A)' }}>
              <div className="w-full h-full rounded-full overflow-hidden bg-[#FAF6EC] border-2 border-[#FAF6EC]">
                {img(s.photo_url) ? (
                  <img src={img(s.photo_url)} alt={s.crop_name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#1A4331] font-bold">{s.crop_name?.[0]}</div>
                )}
              </div>
            </div>
            <span className="text-[10px] text-[#1A4331] font-medium text-center leading-tight truncate w-full">{s.crop_name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============ Story viewer (a single sprout) ============
function StoryViewer({ sprout: s, onClose, onReported, onProfile }) {
  const [reported, setReported] = useState(false);
  const report = async () => {
    try { await axios.post(`${API}/community/report`, { target_type: 'sprout', target_id: s.id, reason: 'Reported from story' }, { withCredentials: true }); setReported(true); onReported?.(); } catch (e) {}
  };
  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="story-viewer">
      <div className="relative w-full max-w-sm bg-black rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#C9A85B] z-10" />
        <button onClick={onClose} className="absolute top-3 right-3 z-10 bg-black/50 text-white rounded-full p-1.5" data-testid="story-close"><X className="w-4 h-4" /></button>
        {img(s.photo_url) ? (
          <img src={img(s.photo_url)} alt={s.crop_name} className="w-full max-h-[70vh] object-contain bg-black" />
        ) : <div className="w-full h-80 bg-[#1A4331]" />}
        <div className="p-4 bg-gradient-to-t from-black to-black/70 text-white">
          <div className="flex items-center justify-between">
            <button onClick={() => s.author_public && s.author_id && onProfile(s.author_id)} className="text-sm font-semibold">
              {s.crop_name} · <span className="opacity-80">{s.display_name}</span>
            </button>
            <span className="text-[11px] opacity-70">{timeAgo(s.created_at)}</span>
          </div>
          {s.caption && <p className="text-sm mt-1 opacity-90">{s.caption}</p>}
          {!s.is_mine && (
            <button onClick={report} disabled={reported} className="mt-3 flex items-center gap-1 text-xs text-white/70 hover:text-white" data-testid="story-report">
              <Flag className="w-3 h-3" /> {reported ? 'Reported' : 'Report'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Post detail modal (comments + nudge + report/block) ============
export function PostDetailModal({ postId, user, onClose, onOpenProfile, onUpgrade }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [menu, setMenu] = useState(false);
  const premium = isPremiumUser(user);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/community/posts/${postId}/comments`, { withCredentials: true });
      setComments(r.data || []);
    } catch (e) { setComments([]); }
  }, [postId]);
  useEffect(() => { if (premium) load(); }, [load, premium]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true); setNotice('');
    try {
      const r = await axios.post(`${API}/community/posts/${postId}/comments`, { body: text.trim() }, { withCredentials: true });
      setText('');
      if (r.data.under_review) setNotice('Your comment is under review and will appear once approved.');
      load();
    } catch (e) {} finally { setBusy(false); }
  };

  const report = async () => { try { await axios.post(`${API}/community/report`, { target_type: 'post', target_id: postId, reason: 'Reported from feed' }, { withCredentials: true }); setNotice('Thanks — our team will review this.'); setMenu(false); } catch (e) {} };

  return (
    <div className="fixed inset-0 bg-black/40 z-[55] flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="post-detail-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Comments</h3>
          <div className="flex items-center gap-2 relative">
            <button onClick={() => setMenu((m) => !m)} data-testid="post-menu-button" className="text-[#7A6B57] hover:text-[#1A4331] text-xl leading-none px-2">⋯</button>
            {menu && (
              <div className="absolute right-0 top-8 bg-white rounded-xl shadow-lg border border-[#1A4331]/10 py-1 z-10 w-40">
                <button onClick={report} data-testid="report-post-button" className="w-full text-left px-3 py-2 text-sm text-[#C66B4A] hover:bg-[#F0F2EB] flex items-center gap-2"><Flag className="w-3.5 h-3.5" /> Report post</button>
              </div>
            )}
            <button onClick={onClose} className="text-[#7A6B57] hover:text-[#1A4331]"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {!premium ? (
          <UpgradeInline onUpgrade={onUpgrade} label="Join the club to read and write comments" />
        ) : (
          <>
            {notice && <div className="mb-3 text-xs bg-[#C9A85B]/15 text-[#1A4331] rounded-xl px-3 py-2" data-testid="post-notice">{notice}</div>}
            <div className="space-y-3 mb-4">
              {comments.length === 0 ? (
                <p className="text-sm text-[#7A6B57] italic">No comments yet — be the first to say something kind.</p>
              ) : comments.map((c) => (
                <div key={c.id} className="flex gap-2.5" data-testid={`comment-${c.id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#6B9F4A]/20 flex items-center justify-center text-[#1A4331] text-xs font-bold flex-shrink-0">{c.display_name?.[0]}</div>
                  <div className="flex-1 bg-white rounded-2xl px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button onClick={() => c.author_public && c.author_id && onOpenProfile(c.author_id)} className="text-xs font-semibold text-[#1A4331]">{c.display_name}</button>
                      <span className="text-[10px] text-[#7A6B57]">{timeAgo(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-[#2A3528] mt-0.5">{c.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={submit} className="flex items-center gap-2 sticky bottom-0">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a comment…" data-testid="comment-input" className="flex-1 px-4 py-2.5 bg-white rounded-full border border-[#1A4331]/10 outline-none focus:border-[#1A4331] text-sm" />
              <button type="submit" disabled={busy || !text.trim()} data-testid="comment-submit" className="bg-[#1A4331] text-[#FAF6EC] rounded-full p-2.5 disabled:opacity-50"><Send className="w-4 h-4" /></button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ============ Grower profile modal ============
export function GrowerProfileModal({ userId, onClose }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [gallery, setGallery] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    axios.get(`${API}/profiles/${userId}`, { withCredentials: true })
      .then((r) => setP(r.data)).catch((e) => setErr(e.response?.data?.detail || 'Could not load profile'));
    axios.get(`${API}/community/follow-status/${userId}`, { withCredentials: true })
      .then((r) => { setFollowing(!!r.data.following); setFollowers(r.data.followers || 0); }).catch(() => {});
    axios.get(`${API}/profiles/${userId}/gallery`, { withCredentials: true })
      .then((r) => setGallery(r.data?.tiles || [])).catch(() => setGallery([]));
  }, [userId]);
  const block = async () => { try { await axios.post(`${API}/community/block`, { user_id: userId }, { withCredentials: true }); setBlocked(true); } catch (e) {} };
  const toggleFollow = async () => {
    try {
      if (following) { await axios.delete(`${API}/community/follow/${userId}`, { withCredentials: true }); setFollowing(false); setFollowers((n) => Math.max(0, n - 1)); }
      else { await axios.post(`${API}/community/follow`, { user_id: userId }, { withCredentials: true }); setFollowing(true); setFollowers((n) => n + 1); }
    } catch (e) {}
  };
  const deleteTile = async (tile) => {
    if (!tile || deleting) return;
    if (!window.confirm('Delete this photo from your gallery? This can\u2019t be undone.')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/profiles/me/gallery/tile`, {
        withCredentials: true,
        data: { source: tile.source, post_id: tile.post_id, sprout_id: tile.sprout_id, photo_url: tile.photo_url, media_type: tile.media_type },
      });
      setGallery((g) => g.filter((x) => x.id !== tile.id));
      setLightbox(null);
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Could not delete this photo.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[58] flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-md max-h-[85vh] overflow-y-auto hg-zoom-in" onClick={(e) => e.stopPropagation()} data-testid="grower-profile-modal">
        <button onClick={onClose} className="absolute top-4 right-4 text-[#7A6B57] hover:text-[#1A4331]"><X className="w-5 h-5" /></button>
        {err ? (
          <p className="text-sm text-[#7A6B57] py-8 text-center">{err}</p>
        ) : !p ? (
          <div className="flex justify-center py-10"><div className="w-10 h-10 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin" /></div>
        ) : (
          <>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden bg-[#6B9F4A]/20 flex items-center justify-center flex-shrink-0">
                {p.avatar ? <img src={p.avatar} alt={p.display_name} className="w-full h-full object-cover" /> : <span className="text-2xl font-bold text-[#1A4331]">{p.display_name?.[0]}</span>}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.display_name}</h3>
                <p className="text-sm text-[#7A6B57]">{p.region}</p>
                <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-[#C9A85B]"><Award className="w-3.5 h-3.5" /> Lvl {p.stats?.level?.level} · {p.stats?.level?.title}</span>
              </div>
            </div>
            {p.bio && <p className="text-sm text-[#2A3528] mt-3 italic">"{p.bio}"</p>}

            <div className="grid grid-cols-3 gap-2 mt-4">
              <Stat label="XP" value={p.stats?.xp} />
              <Stat label="Harvests" value={p.stats?.harvests_logged} />
              <Stat label="Plantings" value={p.stats?.plantings} />
            </div>

            {p.stats?.top_crops?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#7A6B57] mb-2">Top crops</p>
                <div className="flex flex-wrap gap-2">
                  {p.stats.top_crops.map((c) => <span key={c.name} className="bg-[#6B9F4A]/10 text-[#1A4331] text-xs px-3 py-1.5 rounded-full font-medium">{c.name} · {c.count}</span>)}
                </div>
              </div>
            )}

            {p.viewer_skill_tree_enabled !== false && p.stats?.skills?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-bold uppercase tracking-wide text-[#7A6B57] mb-2">Skill tree <span className="text-[#C9A85B]">· {p.stats.skill_overall}% overall</span></p>
                <div className="space-y-2">
                  {p.stats.skills.map((sk) => (
                    <div key={sk.label} data-testid={`skill-${sk.label}`}>
                      <div className="flex justify-between text-xs text-[#2A3528] mb-0.5"><span>{sk.label}</span><span className="text-[#7A6B57]">{sk.score}</span></div>
                      <div className="h-2 bg-[#E5EBDD] rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${sk.score}%`, background: 'linear-gradient(90deg,#6B9F4A,#C9A85B)' }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {gallery.length > 0 && (
              <div className="mt-5" data-testid="profile-gallery">
                <p className="text-xs font-bold uppercase tracking-wide text-[#7A6B57] mb-2">Garden gallery · {gallery.length}</p>
                {Object.entries(gallery.reduce((acc, t) => { (acc[t.group || 'Garden'] = acc[t.group || 'Garden'] || []).push(t); return acc; }, {})).map(([label, items]) => (
                  <div key={label} className="mb-4">
                    <p className="text-[11px] font-semibold text-[#2A3528] mb-1.5">{label}</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {items.map((t) => (
                        <button key={t.id} onClick={() => setLightbox(t)} data-testid={`gallery-tile-${t.id}`} className="relative aspect-square rounded-xl overflow-hidden bg-[#E5EBDD]">
                          {t.media_type === 'video' ? (
                            <div className="w-full h-full flex items-center justify-center bg-[#1A4331]"><Play className="w-6 h-6 text-[#FAF6EC]" fill="#FAF6EC" /></div>
                          ) : (
                            <img src={mediaSrc(t.photo_url)} alt={t.crop_name} className="w-full h-full object-cover" />
                          )}
                          {t.media_type === 'video' && <span className="absolute top-1 right-1 bg-black/60 rounded-full p-1"><Play className="w-2.5 h-2.5 text-white" fill="#fff" /></span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!p.is_self && (
              <div className="mt-5 space-y-2">
                <button onClick={toggleFollow} data-testid="follow-button" className={`w-full flex items-center justify-center gap-2 text-sm font-semibold rounded-full py-2.5 transition-colors ${following ? 'bg-[#6B9F4A]/15 text-[#1A4331]' : 'bg-[#1A4331] text-[#FAF6EC] hover:bg-[#0F2D1F]'}`}>
                  {following ? <><UserCheck className="w-4 h-4" /> Following · {followers}</> : <><UserPlus className="w-4 h-4" /> Follow{followers ? ` · ${followers}` : ''}</>}
                </button>
                <button onClick={block} disabled={blocked} data-testid="block-grower-button" className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-[#C66B4A] border border-[#C66B4A]/30 rounded-full py-2.5 disabled:opacity-50">
                  <Ban className="w-4 h-4" /> {blocked ? 'Blocked' : 'Block this grower'}
                </button>
              </div>
            )}
            {lightbox && (
              <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
                <div className="max-w-lg w-full bg-[#FAF6EC] rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  {lightbox.media_type === 'video' ? (
                    <video src={mediaSrc(lightbox.video_url)} controls autoPlay className="w-full max-h-[60vh] bg-black" data-testid="gallery-lightbox-video" />
                  ) : (
                    <img src={mediaSrc(lightbox.photo_url)} alt={lightbox.crop_name} className="w-full max-h-[60vh] object-contain bg-black" />
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-bold text-[#1A4331]">{lightbox.crop_name}{lightbox.variety ? ` · ${lightbox.variety}` : ''}</p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {p.is_self && (
                          <button onClick={() => deleteTile(lightbox)} disabled={deleting} data-testid="gallery-delete-photo" title="Delete photo" className="text-[#C66B4A] hover:text-[#A8482B] disabled:opacity-50"><Trash2 className="w-5 h-5" /></button>
                        )}
                        <button onClick={() => setLightbox(null)} className="text-[#7A6B57] hover:text-[#1A4331]" data-testid="gallery-lightbox-close"><X className="w-5 h-5" /></button>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-1.5 flex-wrap">
                      {lightbox.family && <span className="text-xs bg-[#6B9F4A]/10 text-[#1A4331] px-2.5 py-1 rounded-full font-medium">{lightbox.family}</span>}
                      {lightbox.season && <span className="text-xs bg-[#6B9F4A]/10 text-[#1A4331] px-2.5 py-1 rounded-full font-medium">{lightbox.season} {lightbox.year}</span>}
                    </div>
                    {lightbox.caption && <p className="text-sm text-[#2A3528] italic mt-2">&quot;{lightbox.caption}&quot;</p>}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-3 text-center">
      <p className="text-lg font-bold text-[#1A4331]">{value ?? 0}</p>
      <p className="text-[10px] uppercase tracking-wide text-[#7A6B57]">{label}</p>
    </div>
  );
}

function UpgradeInline({ onUpgrade, label }) {
  return (
    <div className="text-center py-6" data-testid="upgrade-inline">
      <Lock className="w-8 h-8 text-[#C9A85B] mx-auto mb-2" />
      <p className="text-sm text-[#1A4331] font-semibold">{label}</p>
      <button onClick={onUpgrade} className="mt-3 bg-[#C9A85B] hover:bg-[#B8974A] text-[#1A4331] font-semibold px-5 py-2.5 rounded-full text-sm">Upgrade to Join the Club</button>
    </div>
  );
}

// ============ Premium teaser (blurred) for non-members ============
export function CommunityTeaser({ onUpgrade }) {
  const [preview, setPreview] = useState(null);
  useEffect(() => { axios.get(`${API}/community/preview`, { withCredentials: true }).then((r) => setPreview(r.data)).catch(() => {}); }, []);
  return (
    <div className="relative rounded-3xl overflow-hidden border border-[#1A4331]/10" data-testid="community-teaser">
      {/* Blurred preview cards */}
      <div className="p-6 blur-sm select-none pointer-events-none" aria-hidden>
        <div className="grid sm:grid-cols-2 gap-3">
          {(preview?.teaser_cards || [{}, {}, {}, {}]).map((c, i) => (
            <div key={i} className="bg-[#FAF6EC] rounded-2xl p-4 border border-[#1A4331]/10">
              <p className="text-xs font-semibold text-[#1A4331]">{c.crop || 'Tomato'} · <span className="text-[#7A6B57]">{c.region || 'Canterbury, NZ'}</span></p>
              <p className="text-sm text-[#2A3528] mt-1">{c.snippet || 'Best year yet — mulched heavily and watered deeply twice a week…'}</p>
            </div>
          ))}
        </div>
      </div>
      {/* CTA overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A4331]/95 via-[#1A4331]/70 to-[#1A4331]/40 flex flex-col items-center justify-center text-center p-6">
        <div className="w-14 h-14 rounded-full bg-[#C9A85B] flex items-center justify-center mb-3"><Sparkles className="w-6 h-6 text-[#1A4331]" /></div>
        <h3 className="text-2xl font-bold text-[#FAF6EC]" style={{ fontFamily: 'Outfit, sans-serif' }}>The Verified Grower Community</h3>
        <p className="text-sm text-[#E5EBDD] mt-2 max-w-md">Real harvests, honest tips and answers from growers near you. Ask questions, join challenges, and cheer each other on.</p>
        {preview && (
          <div className="flex items-center gap-4 mt-4 text-[#FAF6EC]">
            <span className="flex items-center gap-1 text-sm"><TrendingUp className="w-4 h-4 text-[#C9A85B]" /> {preview.post_count} posts</span>
            <span className="flex items-center gap-1 text-sm"><Award className="w-4 h-4 text-[#C9A85B]" /> {preview.member_count} public growers</span>
          </div>
        )}
        {preview?.expert_tip_titles?.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            {preview.expert_tip_titles.map((t) => <span key={t} className="bg-[#FAF6EC]/15 text-[#FAF6EC] text-xs px-3 py-1.5 rounded-full">🌱 {t}</span>)}
          </div>
        )}
        <button onClick={onUpgrade} data-testid="teaser-upgrade-button" className="mt-6 bg-[#C9A85B] hover:bg-[#B8974A] text-[#1A4331] font-bold px-8 py-3.5 rounded-full flex items-center gap-2 transition-colors">
          Upgrade to Join the Club <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
